import type { Oferta, Store, Categoria, CtaType, CtaLocation } from "@/types";
import type { ConsentPrefs } from "@/lib/consent";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Wrapper com guard ESTRITO `typeof === "function"` (NÃO truthy).
 * D-55 ANALYTICS-07: uBlock e Brave Shields setam window.gtag para `undefined`,
 * NÃO `null` — guard truthy passaria nesses casos e quebraria.
 */
function safeGtag(...args: unknown[]): void {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag(...args);
    } else if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[gtag] indisponível. Evento não enviado:", args);
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[gtag] error:", err);
    }
  }
}

/**
 * Wrapper safeFbq — mesma política de safeGtag.
 * NOTA: facebookPixel.ts já tem callFbq com retry de 400ms; aqui usamos
 * apenas o guard simples porque trackOfertaClick e trackExternalRedirect
 * são fire-and-forget (não esperam Pixel carregar).
 */
function safeFbq(...args: unknown[]): void {
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq(...args);
    } else if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[fbq] indisponível. Evento não enviado:", args);
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[fbq] error:", err);
    }
  }
}

/**
 * Anexa UTMs a uma URL preservando params já presentes em baseUrl.
 * D-61 ANALYTICS-04: NÃO sobrescreve params existentes — preserva
 * `?tag=primata-20`, `&af_id=...`, `?matt_tool=...` etc.
 *
 * Tipo restrito (`utm_source: "primata"`) força call sites a usar a
 * tag canônica do Primata.
 *
 * NOTA WhatsApp/Telegram: stripam UTMs server-side ao processar invite
 * links. UTMs aqui servem APENAS pro nosso GA4/Pixel logs — NUNCA
 * para Insights do WhatsApp Business (D-62).
 */
export function buildOutboundUrl(
  baseUrl: string,
  params: {
    utm_source: "primata";
    utm_medium: "landing" | "sticky_bar" | "oferta_card" | "redirector";
    utm_campaign: string;
    utm_content?: string;
  }
): string {
  try {
    const url = new URL(baseUrl);
    Object.entries(params).forEach(([key, value]) => {
      if (value && !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });
    return url.toString();
  } catch {
    return baseUrl; // URL inválida → retorna como veio
  }
}

/**
 * CTA principal (WhatsApp/Telegram) clicado em hero / sticky / banner / after_ofertas.
 * Pixel Lead já é disparado pelo trackLeadAndGo do facebookPixel.ts —
 * NÃO duplicar aqui (sibling pattern, ARCHITECTURE.md §3).
 */
export function trackCtaClick(
  type: CtaType,
  location: CtaLocation,
  memberCount?: number,
  eventId?: string,
  variant?: string
): void {
  safeGtag("event", "cta_click", {
    cta_type: type,
    cta_location: location,
    member_count: memberCount ?? null,
    // D-58: espelha o eventId do Pixel Lead p/ dedup futuro (Sprint 3 CAPI)
    event_id: eventId ?? null,
    // CRO P4: variante do experimento de copy dos CTAs (null fora do experimento)
    cta_variant: variant ?? null,
  });
}

/**
 * Card de oferta clicado — dispara TRÊS eventos (D-60):
 *   GA4 card_oferta_click + Pixel ViewContent + Pixel custom OfferClick.
 */
export function trackOfertaClick(oferta: Oferta, position: number): void {
  const discountPct = Math.round(
    ((oferta.priceOriginal - oferta.priceCurrent) / oferta.priceOriginal) * 100
  );
  // Preço único (Telegram) ou desconto < 1%: não existe "preço riscado" real.
  // Manda null em vez de um riscado === atual, pra não poluir a analytics de desconto.
  const hasDiscount = discountPct >= 1;

  safeGtag("event", "card_oferta_click", {
    produto_slug: oferta.slug,
    loja: oferta.store,
    preco_riscado: hasDiscount ? oferta.priceOriginal : null,
    preco_atual: oferta.priceCurrent,
    desconto_pct: discountPct,
    posicao: position,
    currency: "BRL",
    value: oferta.priceCurrent,
  });

  safeFbq("track", "ViewContent", {
    content_type: "product",
    content_ids: [oferta.slug],
    content_name: oferta.title,
    content_category: oferta.store,
    value: oferta.priceCurrent,
    currency: "BRL",
  });

  safeFbq("trackCustom", "OfferClick", {
    slug: oferta.slug,
    store: oferta.store,
    discount_pct: discountPct,
    position,
  });
}

/**
 * Hit no /r/:slug ANTES do redirect externo.
 * D-75 REDIRECT-07: transport_type "beacon" GARANTE que o evento sai
 * mesmo após window.location.replace() — default <img> pixel é
 * cancelado em 3G mobile e perde 15-30% de conversões.
 */
export function trackExternalRedirect(slug: string, store: Store): void {
  safeGtag("event", "external_redirect", {
    slug,
    destino_loja: store,
    referrer_path: typeof window !== "undefined" ? document.referrer : "",
    transport_type: "beacon",
  });
  safeFbq("trackCustom", "RedirectorHit", { slug, store });
}

/**
 * Sticky bar entrou no viewport pela primeira vez (uma vez por sessão).
 * D-37 ANALYTICS-05: flag-guarded — múltiplas montagens/scrolls não
 * disparam novo evento.
 */
let stickyBarTracked = false;
export function trackStickyBarView(): void {
  if (stickyBarTracked) return;
  stickyBarTracked = true;
  safeGtag("event", "sticky_bar_view");
}

/** CRO P2 — GroupChatPreview entrou no viewport (uma vez por sessão). */
let groupPreviewTracked = false;
export function trackGroupPreviewView(): void {
  if (groupPreviewTracked) return;
  groupPreviewTracked = true;
  safeGtag("event", "group_preview_view");
}

/** CRO P3 — CardGrupoCta (card "SÓ NO GRUPO" na vitrine) entrou no viewport (uma vez por sessão). */
let vitrineCtaTracked = false;
export function trackVitrineCtaView(): void {
  if (vitrineCtaTracked) return;
  vitrineCtaTracked = true;
  safeGtag("event", "vitrine_cta_view");
}

/**
 * D-31 / T-2.1-03: Auditoria de consent para ANPD.
 * Dispara SEMPRE, independente do estado de consent — base legal: legítimo interesse (auditoria ANPD).
 * NÃO é gated pelo próprio consent.
 * Shared eventId (D-58) herdado para dedup futuro com CAPI Phase 3.
 */
export function trackConsentDecision(
  prefs: Pick<ConsentPrefs, "analytics" | "marketing">,
  source: "banner_accept_all" | "banner_reject_all" | "banner_custom",
  eventId?: string
): void {
  safeGtag("event", "consent_decision", {
    analytics: prefs.analytics,
    marketing: prefs.marketing,
    source,
    event_id: eventId ?? null,
  });
}

/**
 * Per D-31 / D-03 — testimonial viewed event. Fires once per card per session (IntersectionObserver).
 */
export function trackTestimonialView(testimonialId: string, position: number, eventId?: string): void {
  safeGtag("event", "testimonial_view", {
    testimonial_id: testimonialId,
    position,
    event_id: eventId ?? null,
  });
}

/** D-37 — PWA install prompt lifecycle (analytics-only; not Pixel) */
export function trackPwaInstallPrompt(
  state: "shown" | "accepted" | "dismissed" | "rejected",
  eventId?: string
): void {
  safeGtag("event", "pwa_install_prompt", { state, event_id: eventId ?? null });
}

/** D-37 — Theme change (manual toggle only, NOT initial system-derived) */
export function trackThemeChange(theme: "light" | "dark" | "system"): void {
  safeGtag("event", "theme_change", { theme });
}

/** D-37 — Category filter chip click */
export function trackCategoryFilter(
  categoria: string,
  eventId?: string
): void {
  safeGtag("event", "category_filter", { categoria, event_id: eventId ?? null });
}

/** D-37 — A/B experiment exposure (fired ONCE per session per experiment) */
export function trackExperimentView(
  experimentId: string,
  variant: string,
  eventId?: string
): void {
  safeGtag("event", "experiment_view", {
    experiment_id: experimentId,
    variant,
    event_id: eventId ?? null,
  });
}

// ─── Phase 4 analytics extensions (Plan 04-01) ──────────────────────────────

/** Plan 04-02 / CUPONS-01 — fires on Cupons.tsx mount post-consent. */
export function trackCuponPageView(payload: {
  loja: Store;
  categoria: Categoria;
  ofertas_count: number;
  eventId?: string;
}): void {
  safeGtag("event", "cupon_page_view", {
    loja: payload.loja,
    categoria: payload.categoria,
    ofertas_count: payload.ofertas_count,
    event_id: payload.eventId ?? null,
  });
}

/** Plan 04-05 / VOTE-04 — fires on first vote (anti-duplo guard upstream in voting.ts). */
export function trackVote(payload: {
  slug: string;
  direction: "up" | "down";
  eventId?: string;
}): void {
  safeGtag("event", "oferta_vote", {
    slug: payload.slug,
    direction: payload.direction,
    event_id: payload.eventId ?? null,
  });
}

/** Plan 04-07 / LIVE-02 — fires on LiveBanner CTA click. Hashes per V7 (host handle correlatable with operator identity per Risk A4). */
export function trackLiveBannerClick(payload: {
  platform_url_hash: string;
  host_handle_hash: string;
  eventId?: string;
}): void {
  safeGtag("event", "live_banner_click", {
    platform_url_hash: payload.platform_url_hash,
    host_handle_hash: payload.host_handle_hash,
    event_id: payload.eventId ?? null,
  });
}
